import { Controller } from 'stimulus';

export default class extends Controller {
    static targets = [
        'uxWidget',
        'uxPanel',
        'uxQuestion',
        'uxNegative',
        'uxThanksPositive',
        'uxThanksNegative',
        'uxMessage',
        'exitOverlay',
        'exitModal',
    ];

    static values = {
        cartQuantity: Number,
        cartJson: String,
        uxEnabled: Boolean,
        exitEnabled: Boolean,
        uxUrl: String,
        exitUrl: String,
        uxDelay: { type: Number, default: 60000 },
    };

    connect() {
        this.sessionId = this.getSessionId();
        this.isSubmitting = false;
        this.selectedExitReason = null;

        if (this.hasCartJsonValue) {
            try {
                const cart = JSON.parse(this.cartJsonValue);
                this.cartQuantityValue = cart.quantity || 0;
            } catch (error) {
                this.cartQuantityValue = 0;
            }
        }

        if (this.uxEnabledValue && this.hasUxWidgetTarget && !this.wasUxShownRecently()) {
            this.initUxTimer();
        }

        if (this.exitEnabledValue && this.cartQuantityValue > 0 && !this.wasExitIntentShown()) {
            this.initExitIntent();
        }

        if (window.EventManager) {
            this.onCartUpdated = (cart) => {
                this.cartQuantityValue = cart && cart.quantity ? cart.quantity : 0;

                if (this.exitEnabledValue && this.cartQuantityValue > 0 && !this.wasExitIntentShown() && !this.onExitIntent) {
                    this.initExitIntent();
                }
            };

            window.EventManager.listen('cart-updated', this.onCartUpdated);
        }
    }

    disconnect() {
        if (window.EventManager && this.onCartUpdated) {
            window.EventManager.vue.$off('cart-updated', this.onCartUpdated);
        }
        this.clearUxTimer();
        this.unbindExitIntent();
    }

    closeUx() {
        this.hideUxWidget();
        this.markUxShown();
    }

    submitThumbsUp() {
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.postJson(this.uxUrlValue, {
            rating: 1,
            sessionId: this.sessionId,
            pageUrl: window.location.href,
        })
            .then(() => {
                this.showUxStep('positive');
                this.markUxShown();
            })
            .catch(() => {})
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    submitThumbsDown() {
        this.showUxStep('negative');
    }

    submitUxNegative() {
        if (this.isSubmitting || !this.hasUxMessageTarget) {
            return;
        }

        const message = this.uxMessageTarget.value.trim();
        if (!message) {
            this.uxMessageTarget.focus();
            return;
        }

        this.isSubmitting = true;

        this.postJson(this.uxUrlValue, {
            rating: 0,
            message: message,
            sessionId: this.sessionId,
            pageUrl: window.location.href,
        })
            .then(() => {
                this.showUxStep('thanks-negative');
                this.markUxShown();
            })
            .catch(() => {})
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    closeExitModal() {
        this.hideExitModal();
        this.markExitIntentShown();
    }

    selectExitReason(event) {
        const reason = event.currentTarget.dataset.reason;
        if (!reason) {
            return;
        }

        this.selectedExitReason = reason;
        this.exitModalTarget.querySelectorAll('[data-reason]').forEach((button) => {
            button.classList.toggle('is-selected', button.dataset.reason === reason);
        });
    }

    submitExitIntent() {
        if (this.isSubmitting || !this.selectedExitReason) {
            return;
        }

        this.isSubmitting = true;

        this.postJson(this.exitUrlValue, {
            reason: this.selectedExitReason,
            cartItemsCount: this.cartQuantityValue,
            sessionId: this.sessionId,
            pageUrl: window.location.href,
        })
            .then(() => {
                this.hideExitModal();
                this.markExitIntentShown();
            })
            .catch(() => {})
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    initUxTimer() {
        this.clearUxTimer();
        this.uxTimer = window.setTimeout(() => {
            if (!this.wasUxShownRecently()) {
                this.showUxWidget();
            }
        }, this.uxDelayValue);
    }

    initExitIntent() {
        this.lastMouseY = window.innerHeight;
        this.lastMouseX = window.innerWidth / 2;
        this.topZonePx = 80;
        this.leftChromePx = 24;

        this.onMouseMove = (event) => {
            this.lastMouseY = event.clientY;
            this.lastMouseX = event.clientX;
        };

        this.onExitIntent = (event) => {
            if (this.wasExitIntentShown()) {
                return;
            }

            if (event.relatedTarget !== null) {
                return;
            }

            if (event.clientY > 5) {
                return;
            }

            if (event.clientX < this.leftChromePx || this.lastMouseX < this.leftChromePx) {
                return;
            }

            if (this.lastMouseY > this.topZonePx) {
                return;
            }

            if (event.clientY >= this.lastMouseY) {
                return;
            }

            this.showExitModal();
        };

        document.addEventListener('mousemove', this.onMouseMove, { passive: true });
        document.documentElement.addEventListener('mouseleave', this.onExitIntent);
    }

    unbindExitIntent() {
        if (this.onMouseMove) {
            document.removeEventListener('mousemove', this.onMouseMove);
        }

        if (this.onExitIntent) {
            document.documentElement.removeEventListener('mouseleave', this.onExitIntent);
        }
    }

    clearUxTimer() {
        if (this.uxTimer) {
            window.clearTimeout(this.uxTimer);
            this.uxTimer = null;
        }
    }

    showUxWidget() {
        this.clearUxTimer();
        this.uxWidgetTarget.classList.add('is-visible');
        this.showUxStep('question');
    }

    hideUxWidget() {
        this.uxWidgetTarget.classList.remove('is-visible');
    }

    showUxStep(step) {
        const steps = {
            question: this.hasUxQuestionTarget ? this.uxQuestionTarget : null,
            negative: this.hasUxNegativeTarget ? this.uxNegativeTarget : null,
            positive: this.hasUxThanksPositiveTarget ? this.uxThanksPositiveTarget : null,
            'thanks-negative': this.hasUxThanksNegativeTarget ? this.uxThanksNegativeTarget : null,
        };

        Object.keys(steps).forEach((key) => {
            if (steps[key]) {
                steps[key].classList.toggle('d-none', key !== step);
            }
        });
    }

    showExitModal() {
        if (this.wasExitIntentShown()) {
            return;
        }

        this.exitOverlayTarget.classList.add('is-visible');
        this.exitModalTarget.classList.add('is-visible');
        document.body.classList.add('site-feedback-modal-open');
    }

    hideExitModal() {
        this.exitOverlayTarget.classList.remove('is-visible');
        this.exitModalTarget.classList.remove('is-visible');
        document.body.classList.remove('site-feedback-modal-open');
    }

    postJson(url, payload) {
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }).then((response) => response.json().then((data) => {
            if (!response.ok) {
                throw new Error(data.error || 'Request failed');
            }

            return data;
        }));
    }

    getSessionId() {
        const storageKey = 'benu_site_feedback_session';
        let sessionId = localStorage.getItem(storageKey);

        if (!sessionId) {
            sessionId = 'sf_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
            localStorage.setItem(storageKey, sessionId);
        }

        return sessionId;
    }

    wasUxShownRecently() {
        const stored = localStorage.getItem('benu_site_feedback_ux_date');
        if (!stored) {
            return false;
        }

        return stored === new Date().toISOString().slice(0, 10);
    }

    markUxShown() {
        localStorage.setItem('benu_site_feedback_ux_date', new Date().toISOString().slice(0, 10));
        this.hideUxWidget();
    }

    wasExitIntentShown() {
        return sessionStorage.getItem('benu_site_feedback_exit') === '1';
    }

    markExitIntentShown() {
        sessionStorage.setItem('benu_site_feedback_exit', '1');
    }
}
